import { useQuery } from "@tanstack/react-query";
import { apiGet, apiPost, apiPatch, apiDelete, billingGet } from "~/shared/api/client";
import { createMutationHook } from "~/shared/api/query-factories";
import { retryTransient } from "~/shared/api/retry-transient";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Workspace {
    id: string;
    name: string;
    status: string;
    createdAt: string;
    isPersonal: boolean;
    ownerUserId: string;
}

export interface WorkspaceDetailed {
    workspace: Workspace;
    memberCount: number;
    projectCount: number;
    userRole: string;
    isOwner: boolean;
}

export interface WorkspaceMember {
    id: string;
    userId: string;
    role: string;
    user?: Record<string, unknown> | null;
    /** Resolved user profile from the backend (camelCase via client mapping). */
    email?: string | null;
    name?: string | null;
    firstName?: string | null;
    surname?: string | null;
    image?: string | null;
    [key: string]: unknown;
}

interface WorkspaceBalance {
    balance: number;
    locked: number;
}

export interface WorkspaceBalanceEntry {
    workspaceId: string;
    balance: number;
    locked: number;
}

interface BalanceBatchResponse {
    balances: WorkspaceBalanceEntry[];
}

interface WorkspaceCurrent {
    workspace: Workspace;
    members: WorkspaceMember[];
    [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const workspaceKeys = {
    all: ["workspace"] as const,
    list: ["workspace", "list"] as const,
    detailed: ["workspace", "detailed"] as const,
    current: (workspaceId?: string) => ["workspace", "current", workspaceId] as const,
    members: (workspaceId: string) => ["workspace", "members", workspaceId] as const,
    balances: (workspaceId: string) => ["workspace", "balances", workspaceId] as const,
    balancesBatch: (workspaceIds: string[]) =>
        ["workspace", "balances", "batch", [...workspaceIds].sort().join(",")] as const,
};

// ---------------------------------------------------------------------------
// Fetch functions
// ---------------------------------------------------------------------------

async function fetchUserWorkspaces(): Promise<Workspace[]> {
    return apiGet<Workspace[]>("/v2/workspaces");
}

async function fetchUserWorkspacesDetailed(): Promise<WorkspaceDetailed[]> {
    const data = await apiGet<Record<string, unknown>[]>("/v2/workspaces/detailed");
    return (data ?? []).map((w) => ({
        workspace: {
            id: w.id as string,
            name: w.name as string,
            status: w.status as string,
            createdAt: w.createdAt as string,
            isPersonal: (w.isPersonal as boolean) ?? false,
            ownerUserId: (w.ownerUserId as string) ?? "",
        },
        memberCount: (w.memberCount as number) ?? 0,
        projectCount: (w.projectCount as number) ?? 0,
        userRole: (w.userRole as string) ?? "regular",
        isOwner: (w.isOwner as boolean) ?? false,
    }));
}

async function fetchCurrentWorkspace(workspaceId?: string): Promise<WorkspaceCurrent | null> {
    const data = await apiGet<Record<string, unknown>>("/v2/workspaces/current", {
        workspaceId,
    });
    if (!data?.workspace) return null;

    // Подписи участников — одним запросом на весь список вместо запроса на человека.
    // Сбой связи здесь неотличим от «пользователя удалили», поэтому запрос повторяется
    // при временной ошибке; отказ по существу разбирает сам бэкенд — на неизвестный
    // идентификатор он отвечает коротким префиксом вместо почты, а не пропуском строки.
    const members = (data.members as WorkspaceMember[]) ?? [];
    const memberIds = members.map((m) => m.userId);
    const names = memberIds.length
        ? await retryTransient(() =>
              apiPost<Record<string, { email: string; name: string | null; deleted: boolean }>>(
                  "/v2/users/batch/names",
                  memberIds,
              ),
          )
        : {};
    const enrichedMembers = members.map((m) => ({ ...m, user: names[m.userId] ?? null }));

    return { ...data, workspace: data.workspace as Workspace, members: enrichedMembers };
}

async function fetchWorkspaceBalances(workspaceId: string): Promise<WorkspaceBalance[]> {
    const data = await billingGet<WorkspaceBalance | WorkspaceBalance[]>(`/billing/balances/${workspaceId}`);
    // Backend returns single object, wrap in array for compatibility
    return Array.isArray(data) ? data : [data];
}

async function fetchWorkspaceBalancesBatch(
    workspaceIds: string[],
): Promise<Map<string, WorkspaceBalanceEntry>> {
    const data = await apiPost<BalanceBatchResponse>("/v2/billing/balances/batch", {
        workspaceIds,
    });
    const result = new Map<string, WorkspaceBalanceEntry>();
    for (const entry of data.balances ?? []) {
        result.set(entry.workspaceId, entry);
    }
    return result;
}

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

export function useUserWorkspaces() {
    return useQuery({ queryKey: workspaceKeys.list, queryFn: fetchUserWorkspaces });
}

export function useUserWorkspacesDetailed() {
    return useQuery({ queryKey: workspaceKeys.detailed, queryFn: fetchUserWorkspacesDetailed });
}

export function useCurrentWorkspace(workspaceId?: string) {
    return useQuery({
        queryKey: workspaceKeys.current(workspaceId),
        queryFn: () => fetchCurrentWorkspace(workspaceId),
    });
}

async function fetchWorkspaceMembers(workspaceId: string): Promise<WorkspaceMember[]> {
    return apiGet<WorkspaceMember[]>(`/v2/workspaces/${workspaceId}/members`);
}

export function useWorkspaceMembers(workspaceId: string | undefined) {
    return useQuery({
        queryKey: workspaceKeys.members(workspaceId ?? ""),
        queryFn: () => fetchWorkspaceMembers(workspaceId!),
        enabled: !!workspaceId,
    });
}

export function useWorkspaceBalances(workspaceId: string | undefined) {
    return useQuery({
        queryKey: workspaceKeys.balances(workspaceId ?? ""),
        queryFn: () => fetchWorkspaceBalances(workspaceId!),
        enabled: !!workspaceId,
    });
}

export function useWorkspaceBalancesBatch(workspaceIds: string[]) {
    return useQuery({
        queryKey: workspaceKeys.balancesBatch(workspaceIds),
        queryFn: () => fetchWorkspaceBalancesBatch(workspaceIds),
        enabled: workspaceIds.length > 0,
    });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

export const useCreateWorkspace = createMutationHook({
    mutationFn: async (input: { name: string }) => {
        const workspace = await apiPost<Workspace>("/v2/workspaces", { name: input.name });
        return { workspace, message: "common:success.workspace.created" };
    },
    invalidate: [workspaceKeys.all],
});

export const useRemoveMember = createMutationHook({
    mutationFn: async (input: { workspaceId: string; userId: string }) => {
        // Find member ID by userId, then delete
        const members = await apiGet<WorkspaceMember[]>(`/v2/workspaces/${input.workspaceId}/members`);
        const member = (members ?? []).find((m) => m.userId === input.userId);
        if (member) {
            await apiDelete(`/v2/workspaces/${input.workspaceId}/members/${member.id}`);
        }
        return { success: true };
    },
    invalidate: [workspaceKeys.all],
});

export const useUpdateMemberRole = createMutationHook({
    mutationFn: async (input: { workspaceId: string; userId: string; newRole: "admin" | "regular" }) => {
        const members = await apiGet<WorkspaceMember[]>(`/v2/workspaces/${input.workspaceId}/members`);
        const member = (members ?? []).find((m) => m.userId === input.userId);
        if (member) {
            return apiPatch(`/v2/workspaces/${input.workspaceId}/members/${member.id}`, {
                role: input.newRole,
            });
        }
        return null;
    },
    invalidate: [workspaceKeys.all],
});

export const useUpdateWorkspace = createMutationHook({
    mutationFn: (input: { workspaceId: string; name: string }) =>
        apiPatch(`/v2/workspaces/${input.workspaceId}`, { name: input.name }),
    invalidate: [workspaceKeys.all],
});

export const useDeleteWorkspace = createMutationHook({
    mutationFn: (input: { workspaceId: string }) =>
        apiPost<{ message: string }>(`/v2/workspaces/${input.workspaceId}/delete-cascade`, {}),
    invalidate: [workspaceKeys.all],
});

export const useInviteMember = createMutationHook({
    mutationFn: async (input: { workspaceId: string; email: string; role: "admin" | "regular" }) => {
        const result = await apiPost<Record<string, unknown>>(
            `/v2/workspaces/${input.workspaceId}/invitations`,
            { email: input.email, role: input.role },
        );
        return { type: "created" as const, ...result };
    },
    invalidate: [workspaceKeys.all],
});

export const useAcceptInvitation = createMutationHook({
    mutationFn: (input: { token: string }) => apiPost(`/v2/workspaces/invitations/${input.token}/accept`, {}),
    invalidate: [workspaceKeys.all],
});
